import Swiper from 'swiper';
import {
    A11y,
    Autoplay,
    EffectFade,
    Navigation,
    Pagination,
    Thumbs
} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/a11y';
import 'swiper/css/effect-fade';
import 'swiper/css/effect-flip';

const accreditationSwipers = new Map();

function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function getAccreditationSlides(sliderEl) {
    return Array.from(
        sliderEl.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)')
    );
}

function getGapValue(wrapper) {
    if (!wrapper) return 0;
    const styles = window.getComputedStyle(wrapper);
    const gap = styles.columnGap || styles.gap || '0';
    return Number.parseFloat(gap) || 0;
}

function shouldInitAccreditations(sliderEl) {
    const wrapper = sliderEl.querySelector('.swiper-wrapper');
    const slides = getAccreditationSlides(sliderEl);
    if (!wrapper || !slides.length) return false;

    const containerWidth = sliderEl.clientWidth;
    if (!containerWidth) return false;

    const gapValue = getGapValue(wrapper) || 40;
    const slidesWidth = slides.reduce((sum, slide) => {
        return sum + slide.getBoundingClientRect().width;
    }, 0);
    const totalWidth = slidesWidth + gapValue * Math.max(slides.length - 1, 0);
    const wrapperWidth = wrapper.scrollWidth;
    const measuredWidth = Math.max(totalWidth, wrapperWidth);

    return measuredWidth > containerWidth;
}

function setAutoplayToggleState(toggleButton, isEnabled, isPlaying) {
    if (!toggleButton) return;

    toggleButton.disabled = !isEnabled;
    toggleButton.setAttribute('aria-disabled', isEnabled ? 'false' : 'true');
    toggleButton.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');

    const srText = toggleButton.querySelector('.slick-sr-only');
    if (srText) {
        srText.textContent = isEnabled
            ? (isPlaying ? 'Pause autoplay' : 'Play autoplay')
            : 'Autoplay disabled';
    }
}

function initAccreditations(sliderEl) {
    if (accreditationSwipers.has(sliderEl)) return;

    const reduce = prefersReducedMotion();
    const container = sliderEl.closest('.swiperAccreditationsBlock');
    const toggleButton = container
        ? container.querySelector('.js-accreditations-autoplay-toggle')
        : null;
    
    const instance = new Swiper(sliderEl, {
        modules: [A11y, Autoplay],
        a11y: {
            enabled: true
        },
        centeredSlides: true,
        watchOverflow: true,
        speed: reduce ? 400 : 6000,
        loop: !reduce,
        allowTouchMove: true,
        navigation: false,
        autoplay: {
            enabled: true,
            delay: 0,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
        },
        breakpoints: {
            0: {
                slidesPerView: 3,
                spaceBetween: 40,
            },
            576: {
                slidesPerView: 4,
                spaceBetween: 40,
            },
            992: {
                slidesPerView: 7,
                spaceBetween: 40,
            },
        },
    });

    if (reduce) {
        instance.autoplay.stop();
    }

    if (toggleButton) {
        if (!toggleButton.dataset.accreditationsToggleBound) {
            toggleButton.addEventListener('click', () => {
                if (toggleButton.disabled) return;
                const currentInstance = accreditationSwipers.get(sliderEl);
                if (!currentInstance) return;
                if (currentInstance.autoplay.running) {
                    currentInstance.autoplay.stop();
                } else {
                    currentInstance.autoplay.start();
                }
                setAutoplayToggleState(
                    toggleButton,
                    true,
                    currentInstance.autoplay.running
                );
            });
            toggleButton.dataset.accreditationsToggleBound = 'true';
        }

        setAutoplayToggleState(toggleButton, true, !reduce);
    }

    accreditationSwipers.set(sliderEl, instance);
}

function destroyAccreditations(sliderEl) {
    const instance = accreditationSwipers.get(sliderEl);
    if (!instance) return;

    instance.destroy(true, true);
    accreditationSwipers.delete(sliderEl);
}

function syncAccreditations() {
    document.querySelectorAll('.swiperAccreditationsSlider').forEach((sliderEl) => {
        const container = sliderEl.closest('.swiperAccreditationsBlock');
        const toggleButton = container
            ? container.querySelector('.js-accreditations-autoplay-toggle')
            : null;
        const shouldInit = shouldInitAccreditations(sliderEl);

        sliderEl.classList.toggle('is-static', !shouldInit);

        if (shouldInit) {
            initAccreditations(sliderEl);
            const instance = accreditationSwipers.get(sliderEl);
            if (instance) {
                setAutoplayToggleState(
                    toggleButton,
                    true,
                    instance.autoplay.running
                );
            }
        } else {
            destroyAccreditations(sliderEl);
            setAutoplayToggleState(toggleButton, false, false);
        }
    });
}

syncAccreditations();
window.addEventListener('resize', syncAccreditations);
window.addEventListener('load', syncAccreditations);

document.querySelectorAll('.swiperCenteredSlider').forEach(function (thisSlider) {
    const id = thisSlider.id; // make sure your slider element has a unique ID
    const slider = document.getElementById(id);
    const container = slider.closest('.swiperCenteredBlock');
    const nextButtons = Array.from(container.querySelectorAll('.swiperCenteredNext'));
    const prevButtons = Array.from(container.querySelectorAll('.swiperCenteredPrev'));

    const swiper = new Swiper(slider, {
        modules: [Navigation, A11y, Pagination],
        a11y: {
            enabled: true
        },
        centeredSlides: true,
        loop: true,
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
        navigation: {
            nextEl: nextButtons,
            prevEl: prevButtons,
        },
        on: {
            init(instance) {
                requestAnimationFrame(() => {
                    instance.update();
                    instance.slideToLoop(instance.realIndex, 0, false);
                });
            },
        },
        breakpoints: {
            0: {
                slidesPerView: 1,
                spaceBetween: 16,
            },
            992: {
                slidesPerView: 1.2,
                spaceBetween: 32,
            },
            1200: {
                slidesPerView: 1.2,
                spaceBetween: 48,
            },
        },
    });
});

document.querySelectorAll('.swiperTabbedSlider').forEach(function (thisSlider) {
    const id = thisSlider.id; // make sure your slider element has a unique ID
    const slider = document.getElementById(id);
    const container = slider.closest('.swiperTabbedBlock');
    const nextButtons = Array.from(container.querySelectorAll('.swiperTabbedNext'));
    const prevButtons = Array.from(container.querySelectorAll('.swiperTabbedPrev'));

    const swiper = new Swiper(slider, {
        modules: [Navigation, A11y],
        a11y: {
            enabled: true
        },
        navigation: {
            nextEl: nextButtons,
            prevEl: prevButtons,
        },
        breakpoints: {
            0: {
                slidesPerView: 1.1,
                spaceBetween: 16,
            },
            576: {
                slidesPerView: 2.1,
                spaceBetween: 16,
            },
            992: {
                slidesPerView: 3,
                spaceBetween: 16,
            },
        },
    });
});

document.querySelectorAll('.swiperEventsSlider').forEach(function (thisSlider) {
    const id = thisSlider.id; // make sure your slider element has a unique ID
    const slider = document.getElementById(id);
    const container = slider.closest('.swiperEventsBlock');
    const nextButtons = Array.from(container.querySelectorAll('.swiperEventsNext'));
    const prevButtons = Array.from(container.querySelectorAll('.swiperEventsPrev'));

    const swiper = new Swiper(slider, {
        modules: [Navigation, A11y],
        a11y: {
            enabled: true
        },
        navigation: {
            nextEl: nextButtons,
            prevEl: prevButtons,
        },
        breakpoints: {
            0: {
                slidesPerView: 1,
                spaceBetween: 16,
            },
            576: {
                slidesPerView: 2.1,
                spaceBetween: 16,
            },
            992: {
                slidesPerView: 2.5,
                spaceBetween: 16,
            },
        },
    });
});
